import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { LeadDateFilter, DownloadBtn } from "../../components/crm/LeadDateFilter";
import { rangeFor, inRange, exportContactsCsv } from "./leadExport";
import {
  ArrowsClockwise, Funnel, Trash, EnvelopeSimple, Phone, Calendar, User,
  X, ChatCircleDots, Buildings, Globe, ChartLineUp, Lightning,
  ArrowSquareOut, WhatsappLogo,
} from "@phosphor-icons/react";

const STATUS_STYLE = {
  new:        "bg-[#FCEEEF] text-[#7A1F26] border border-[#E63946]/20",
  contacted:  "bg-[#F9E4B7] text-[#7A5A11]",
  interested: "bg-[#FCD9DD] text-[#7A2E18]",
  qualified:  "bg-[#D4E0D1] text-[#1A3317]",
  converted:  "bg-[#2D5A27] text-white",
  won:        "bg-[#2D5A27] text-white",
  lost:       "bg-[#E5E2D9] text-[#5C5A55]",
  spam:       "bg-[#F7F6F2] text-[#8A8780] line-through",
};

const ROLE_LABEL = {
  customer: "Customer", institution: "Institution", partner: "Partner",
  franchise: "Franchise", government: "Government", other: "Other",
};

const ROLE_TINT = {
  customer: "bg-[#EFF3EE] text-[#1A3317]",
  institution: "bg-[#FCEEEF] text-[#7A1F26]",
  partner: "bg-[#F9E4B7] text-[#7A5A11]",
  franchise: "bg-[#FCD9DD] text-[#7A2E18]",
  government: "bg-[#D7E3F4] text-[#1F3A5F]",
  other: "bg-[#F7F6F2] text-[#5C5A55]",
};

export default function ContactSubmissions() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ status: "", role: "" });
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/leads", { params: {} });
      // Dedicated module — show ONLY public homepage submissions
      setRows((r.data || []).filter((l) => l.source === "homepage"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const { can } = useAuth();
  const canExport = can(["superadmin", "admin"]);
  const [preset, setPreset] = useState("all");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const range = rangeFor(preset, custom);
  const filtered = useMemo(() => rows.filter((r) => {
    if (filter.status && r.status !== filter.status) return false;
    if (filter.role && r.role !== filter.role) return false;
    return inRange(r.created_at, range);
  }), [rows, filter, range[0], range[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (lid, status) => {
    setBusy((b) => ({ ...b, [lid]: true }));
    try {
      await api.post(`/leads/${lid}/status`, { status });
      toast.success(`Submission → ${status}`);
      load();
      if (active?.id === lid) setActive((a) => ({ ...a, status }));
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy((b) => ({ ...b, [lid]: false }));
  };

  const remove = async (lid) => {
    if (!window.confirm("Delete this contact submission permanently?")) return;
    try {
      await api.delete(`/leads/${lid}`);
      toast.success("Deleted");
      setActive(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const stats = useMemo(() => ({
    total: rows.length,
    new: rows.filter((r) => r.status === "new").length,
    institution: rows.filter((r) => r.role === "institution").length,
    franchise: rows.filter((r) => r.role === "franchise").length,
  }), [rows]);

  return (
    <AppShell
      title="Contact Submissions"
      subtitle="Homepage Contact-Form responses · pre-filtered live"
      actions={
        <button onClick={load} data-testid="contact-refresh"
                className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
          <ArrowsClockwise size={14}/>Refresh
        </button>
      }
    >
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Kpi I={ChatCircleDots} label="Total submissions" v={stats.total} />
        <Kpi I={Lightning}      label="Awaiting first contact" v={stats.new} accent="#E63946" />
        <Kpi I={Buildings}      label="Institutions" v={stats.institution} accent="#7A1F26" />
        <Kpi I={ChartLineUp}    label="Franchise interest" v={stats.franchise} accent="#7A5A11" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E2D9] rounded-md mb-5 p-3 flex items-center gap-3 flex-wrap" data-testid="contact-filters">
        <Funnel size={14} className="text-[#5C5A55]" />
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                data-testid="filter-status"
                className="h-9 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
          <option value="">All statuses</option>
          {Object.keys(STATUS_STYLE).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter.role} onChange={(e) => setFilter({ ...filter, role: e.target.value })}
                data-testid="filter-role"
                className="h-9 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
          <option value="">All visitor types</option>
          {Object.keys(ROLE_LABEL).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </select>
        <LeadDateFilter preset={preset} setPreset={setPreset} custom={custom} setCustom={setCustom} testPrefix="contact" />
        <span className="text-[10px] text-[#8A8780] inline-flex items-center gap-1 ml-auto">
          <Globe size={10} /> Source · homepage / Section I lead form
        </span>
        {canExport && (
          <DownloadBtn label={`Download CSV (${filtered.length})`} testid="contact-download-csv" disabled={!filtered.length}
                       onClick={() => exportContactsCsv(filtered, range)} />
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="contact-table">
              <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <tr>
                  <th className="text-left px-4 py-2.5">Visitor</th>
                  <th className="text-left px-3 py-2.5">Role</th>
                  <th className="text-left px-3 py-2.5">Phone</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-right px-4 py-2.5">When</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-[#8A8780]">
                    <ChatCircleDots size={28} className="inline-block mb-2 opacity-40" /><br />
                    No submissions yet — your homepage form will surface inquiries here.
                  </td></tr>
                )}
                {filtered.map((l) => (
                  <tr key={l.id} onClick={() => setActive(l)} data-testid={`contact-row-${l.id}`}
                      className={`border-t border-[#F0ECE3] cursor-pointer hover:bg-[#F7F6F2] ${active?.id === l.id ? "bg-[#EFF3EE]" : ""}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{l.name}</div>
                      {l.organisation && <div className="text-[10px] text-[#8A8780] flex items-center gap-1"><Buildings size={9} />{l.organisation}</div>}
                      {l.email && <div className="text-[10px] text-[#8A8780] flex items-center gap-1"><EnvelopeSimple size={9} />{l.email}</div>}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${ROLE_TINT[l.role] || "bg-[#F7F6F2]"}`}>{ROLE_LABEL[l.role] || l.role}</span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11px]">{l.phone}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[9px] px-2 py-0.5 rounded uppercase tracking-wider ${STATUS_STYLE[l.status] || "bg-[#E5E2D9]"}`}>{l.status}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-[10px] text-[#8A8780]">
                      <div className="inline-flex items-center gap-1"><Calendar size={9} />{(l.created_at || "").slice(0, 10)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail pane */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-5 max-h-[640px] overflow-y-auto" data-testid="contact-detail">
          {!active ? (
            <div className="text-center py-12 text-sm text-[#8A8780]">
              <ChatCircleDots size={28} className="inline-block mb-2 opacity-40" />
              <div>Pick a submission to see the full message and reply</div>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#EFF3EE] flex items-center justify-center"><User size={16} color="#2D5A27" weight="fill"/></div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-lg leading-tight">{active.name}</div>
                  <div className="text-xs text-[#5C5A55]">
                    <span className={`text-[10px] px-2 py-0.5 rounded mr-1.5 ${ROLE_TINT[active.role] || ""}`}>{ROLE_LABEL[active.role] || active.role}</span>
                    {(active.created_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                </div>
                <button onClick={() => setActive(null)} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={14} /></button>
              </div>
              <div className="space-y-2 text-sm">
                {active.phone && <Row I={Phone} v={active.phone} href={`tel:${active.phone}`} />}
                {active.email && <Row I={EnvelopeSimple} v={active.email} href={`mailto:${active.email}`} />}
                {active.organisation && <Row I={Buildings} v={active.organisation} />}
              </div>
              {(active.notes || active.message) && (
                <div className="mt-4 p-3 bg-[#F7F6F2] rounded-md border border-[#F0ECE3]">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1.5 inline-flex items-center gap-1">
                    <ChatCircleDots size={10} /> Message
                  </div>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">{active.notes || active.message}</div>
                </div>
              )}
              {(active.ip || active.user_agent) && (
                <div className="mt-3 pt-3 border-t border-[#F0ECE3]">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-2">Visitor signal</div>
                  {active.ip && <div className="text-[10px] text-[#8A8780] font-mono">IP {active.ip}</div>}
                  {active.user_agent && <div className="text-[10px] text-[#8A8780] line-clamp-2">{active.user_agent}</div>}
                </div>
              )}

              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-2">Update status</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {["new", "contacted", "interested", "qualified", "converted", "lost"].map((s) => (
                    <button key={s} disabled={busy[active.id] || active.status === s}
                            onClick={() => setStatus(active.id, s)}
                            data-testid={`set-status-${s}`}
                            className={`text-[11px] py-2 rounded-md border ${active.status === s ? "border-[#2D5A27] bg-[#EFF3EE]" : "border-[#E5E2D9] hover:bg-[#F7F6F2]"} disabled:opacity-50`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-2">
                {active.phone && (
                  <a href={`https://wa.me/${active.phone.replace(/\D/g, "")}?text=${encodeURIComponent("Hi " + active.name + ", thanks for reaching out via the Rescigo Health website. ")}`}
                     target="_blank" rel="noreferrer" data-testid="reply-whatsapp"
                     className="h-10 inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1ea854] text-white rounded-md text-xs font-medium">
                    <WhatsappLogo size={13} weight="fill" />WhatsApp
                  </a>
                )}
                {active.email && (
                  <a href={`mailto:${active.email}?subject=${encodeURIComponent("Re: your enquiry on Rescigo Health")}`}
                     data-testid="reply-email"
                     className="h-10 inline-flex items-center justify-center gap-1.5 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-xs font-medium">
                    <EnvelopeSimple size={13} />Email
                  </a>
                )}
              </div>
              <button onClick={() => remove(active.id)} data-testid="delete-submission"
                      className="mt-3 w-full h-9 inline-flex items-center justify-center gap-1.5 text-[#B91C1C] text-xs hover:bg-[#FCEEEF] rounded-md">
                <Trash size={11}/>Delete submission
              </button>

              <div className="mt-5 pt-4 border-t border-[#F0ECE3]">
                <a href={`/crm/leads`} target="_blank" rel="noreferrer"
                   className="text-[11px] text-[#5C5A55] inline-flex items-center gap-1 hover:text-[#1A1A1A]">
                  Open in full CRM <ArrowSquareOut size={10} />
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}

const Kpi = ({ I, label, v, accent }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] inline-flex items-center gap-1.5">
      <I size={10} weight="fill" />{label}
    </div>
    <div className="text-2xl font-light tabular-nums mt-1" style={accent ? { color: accent } : undefined}>{v}</div>
  </div>
);

const Row = ({ I, v, href }) => (
  <div className="flex items-center gap-2 text-sm text-[#1A1A1A]">
    <I size={12} className="text-[#5C5A55]" />
    {href ? <a href={href} className="hover:underline">{v}</a> : <span>{v}</span>}
  </div>
);
